import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

const written = (w) => w.action === "created" || w.action === "updated";

/** Infobip per-site connection state + actions (status, DNS matrix, connect/fix/verify/disconnect/test). */
export function useInfobip(site, reload) {
  const [busy, setBusy] = useState(false);
  const [busyRow, setBusyRow] = useState(null);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [status, setStatus] = useState(null);
  const [matrix, setMatrix] = useState([]);
  const connected = Boolean(site.infobip_connected_at) || Boolean(status?.connected);

  const refreshStatus = useCallback(async () => {
    try { const r = await api.get(`/sites/${site.id}/infobip/status`); setStatus(r.data); setErr(""); }
    catch (e) { setErr(e.response?.data?.detail || e.message); }
  }, [site.id]);

  const refreshMatrix = useCallback(async () => {
    try { const r = await api.get(`/sites/${site.id}/infobip/dns-matrix`); setMatrix(r.data.matrix || []); setErr(""); }
    catch (e) { setErr(e.response?.data?.detail || e.message); }
  }, [site.id]);

  useEffect(() => {
    let cancelled = false;
    (async () => { await refreshStatus(); if (!cancelled && site.infobip_connected_at) await refreshMatrix(); })();
    return () => { cancelled = true; };
  }, [site.infobip_connected_at, refreshStatus, refreshMatrix]);

  async function run(fn, row = null) {
    setBusy(true); setBusyRow(row); setErr(""); setMsg("");
    try { await fn(); return true; }
    catch (e) { setErr(e.response?.data?.detail || e.message); return false; }
    finally { setBusy(false); setBusyRow(null); }
  }

  const connect = () => run(async () => {
    const r = await api.post(`/sites/${site.id}/infobip/connect`);
    setMsg(`Connected · ${(r.data.dns_writes || []).filter(written).length} DNS record(s) written · verification triggered`);
    setMatrix(r.data.dns_matrix || []);
    await reload();
    await refreshStatus();
  });

  const fixDns = () => run(async () => {
    const r = await api.post(`/sites/${site.id}/infobip/dns/apply`);
    const writes = r.data.writes || [];
    setMsg(`Fix DNS · wrote ${writes.filter(written).length}, skipped ${writes.filter((w) => w.action === "skip").length} already-ok`);
    setMatrix(r.data.matrix || []);
  }, "all");

  const verifyAll = () => run(async () => {
    const r = await api.post(`/sites/${site.id}/infobip/verify`);
    setMsg("Verification triggered · matrix refreshed");
    setMatrix(r.data.matrix || []);
    await refreshStatus();
  }, "verify");

  const disconnect = () => {
    if (!window.confirm("Disconnect Infobip and DELETE its DKIM / tracking / MX DNS records?")) return;
    return run(async () => {
      await api.delete(`/sites/${site.id}/infobip/disconnect?delete_dns=true`);
      setMsg("Disconnected"); setStatus(null); setMatrix([]);
      await reload();
    });
  };

  const sendTest = (to) => {
    if (!to.trim()) { setErr("Enter a recipient email"); return; }
    return run(async () => {
      const r = await api.post(`/sites/${site.id}/infobip/send-test`, { to_email: to.trim() });
      setMsg(`Sent · ${JSON.stringify(r.data.result).slice(0, 120)}`);
    });
  };

  return { busy, busyRow, msg, err, status, matrix, connected, refreshMatrix, connect, fixDns, verifyAll, disconnect, sendTest };
}
